/**
 * /admin/users/:userId/audit-events — per-user impersonation drilldown
 * (#990). Originally the link target of the "Letzte Admin-Sitzung"
 * column on /admin; that column was removed in #1173, but this drilldown
 * remains a standalone feature reachable by direct URL.
 *
 * Consumes the admin-scoped backend endpoint shipped in subtask 02-990
 * (PR #1007). The backend locks ``actor_type='admin_impersonation'``
 * and ``subject_user_id={user_id}`` server-side (HOF-685: the person
 * impersonated is the subject, the admin the actor) so we never need to send
 * either from the client; this page just forwards the user's
 * impersonator/window filters (none in the MVP — those are a follow-up
 * if reviewers ask for them) and drives forward+back pagination from a
 * client-side cursor stack (UDB's keyset is opaque and forward-only).
 *
 * Reuses the design-system ``AuditLogTable`` for rendering so this
 * surface inherits the same row layout as ``/history``. Its internal
 * client-side pagination is intentionally short-circuited (pageSize
 * larger than a single server page) — server-cursor pagination is the
 * source of truth here. A single page indicator + Prev/Next pair sits
 * below the table.
 */
import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router';
import { useTranslation } from 'react-i18next';
import { ArrowLeft, ChevronLeft, ChevronRight } from 'lucide-react';
import { AuditLogTable } from '@hofsync/design-system';
import { useAdminAuditEvents } from '../hooks/useAdminAuditEvents';
import './AdminAuditDrilldown.css';

// Larger than the server-side limit so the design-system table's
// internal pagination collapses to a single page — cursor pagination
// at the server boundary is the source of truth.
const TABLE_PAGE_SIZE = 1000;

/**
 * Client-side cursor stack for forward+back navigation. ``cursors[0]``
 * is ``undefined`` (the first page — UDB treats absent cursor as
 * "start"). As the admin advances, the ``next_cursor`` returned for
 * page N becomes the entry at ``cursors[N+1]``.
 */
const AdminAuditDrilldown: React.FC = () => {
  const { userId } = useParams<{ userId: string }>();
  const { t } = useTranslation();

  const [pageIndex, setPageIndex] = useState(0);
  const [cursors, setCursors] = useState<(string | undefined)[]>([undefined]);
  const currentCursor = cursors[pageIndex];

  const {
    entries,
    nextCursor,
    isLoading,
    isError,
    refetch,
  } = useAdminAuditEvents({
    userId: userId ?? '',
    cursor: currentCursor,
  });

  // Sync the cached cursor for the next page so Back works without a
  // second round-trip when the admin returns. We replace whenever the
  // server's next_cursor for the current page diverges from what we
  // had stored — that happens when React Query refetches a stale page
  // (audit rows can land between visits, shifting the keyset boundary
  // forward). When that happens, any cursors further out in the stack
  // are also invalidated, so we truncate them; the next forward nav
  // re-fetches and rebuilds from the new boundary. Equality check
  // makes the effect idempotent — once stored, subsequent re-renders
  // are no-ops.
  useEffect(() => {
    if (!nextCursor) return;
    if (cursors[pageIndex + 1] === nextCursor) return;
    setCursors((prev) => {
      const truncated = prev.slice(0, pageIndex + 1);
      truncated.push(nextCursor);
      return truncated;
    });
  }, [nextCursor, pageIndex, cursors]);

  const canGoPrev = pageIndex > 0;
  const canGoNext = nextCursor !== null;

  const handlePrev = () => {
    if (canGoPrev) setPageIndex((i) => i - 1);
  };
  const handleNext = () => {
    if (canGoNext) setPageIndex((i) => i + 1);
  };

  const subtitle = userId
    ? t('admin.auditDrilldown.subtitleFor', 'Impersonation history for {{user}}', {
        user: userId,
      })
    : t('admin.auditDrilldown.subtitleUnknownUser', 'Impersonation history');

  return (
    <div className="admin-audit-drilldown">
      <div className="admin-audit-drilldown__header">
        <Link to="/admin" className="admin-audit-drilldown__back">
          <ArrowLeft size={16} />
          {t('admin.auditDrilldown.backToUsers', 'Back to user list')}
        </Link>
        <h1 className="admin-audit-drilldown__title">
          {t('admin.auditDrilldown.title', 'Admin sessions')}
        </h1>
        <p className="admin-audit-drilldown__subtitle">{subtitle}</p>
      </div>

      {isError ? (
        <div
          role="alert"
          className="admin-audit-drilldown__error"
        >
          <p>
            {t(
              'admin.auditDrilldown.loadError',
              'Audit events could not be loaded.',
            )}
          </p>
          <button type="button" onClick={() => refetch()}>
            {t('admin.auditDrilldown.retry', 'Retry')}
          </button>
        </div>
      ) : (
        <>
          <AuditLogTable
            entries={entries}
            pageSize={TABLE_PAGE_SIZE}
            emptyMessage={
              isLoading
                ? t('admin.auditDrilldown.loading', 'Loading events…')
                : t(
                    'admin.auditDrilldown.emptyState',
                    'No admin impersonations for this user.',
                  )
            }
          />
          <div
            className="admin-audit-drilldown__pagination"
            role="navigation"
            aria-label={t('admin.auditDrilldown.title', 'Admin sessions')}
          >
            <button
              type="button"
              className="admin-audit-drilldown__page-btn"
              onClick={handlePrev}
              disabled={!canGoPrev}
              aria-label={t('admin.auditDrilldown.newerEvents', 'Newer events')}
            >
              <ChevronLeft size={16} />
            </button>
            <span className="admin-audit-drilldown__page-indicator">
              {t('admin.auditDrilldown.pageIndicator', 'Page {{page}}', {
                page: pageIndex + 1,
              })}
            </span>
            <button
              type="button"
              className="admin-audit-drilldown__page-btn"
              onClick={handleNext}
              disabled={!canGoNext}
              aria-label={t('admin.auditDrilldown.olderEvents', 'Older events')}
            >
              <ChevronRight size={16} />
            </button>
          </div>
        </>
      )}
    </div>
  );
};

export default AdminAuditDrilldown;
